import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MoreVertical, Users, Link as LinkIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { useSilentRefresh } from '../lib/useSilentRefresh';
import { Avatar } from '../components/Avatar';
import { toast } from 'sonner';

const firstNameOf = (name) => (name || '').trim().split(' ')[0] || 'this person';

export const Connections = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, refreshUser } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async (silent = false) => {
    try {
      const data = await api.getMyConnections();
      setConnections(data);
    } catch {
      if (!silent) toast.error('Could not load your connections');
    } finally {
      setLoading(false);
    }
  }, []);

  const silentLoad = useCallback(() => load(true), [load]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    load();
  }, [authLoading, isAuthenticated, load, navigate]);

  // Newly joined invitees show up on their own; stops when this screen unmounts.
  useSilentRefresh(silentLoad, !authLoading && isAuthenticated);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await api.removeConnection(confirmTarget.id);
      setConnections((prev) => prev.filter((c) => c.id !== confirmTarget.id));
      setConfirmTarget(null);
      toast.success('Connection removed');
      refreshUser();
    } catch {
      toast.error('Could not remove connection');
    } finally {
      setRemoving(false);
    }
  };

  const firstName = firstNameOf(confirmTarget?.name);
  const FirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);

  return (
    <div className="page" data-testid="connections-page">
      <header className="page-header">
        <div className="connections-header-top">
          <button className="back-btn" onClick={() => navigate('/profile')} aria-label="Back to profile" data-testid="connections-back">
            <ArrowLeft />
          </button>
          <h1 className="page-title">Your connections</h1>
        </div>
        <p className="connections-subtitle">People you share recommendations with.</p>
      </header>

      {loading ? (
        <div className="placeholder" data-testid="connections-loading">
          <p className="placeholder-text">Loading...</p>
        </div>
      ) : connections.length === 0 ? (
        <div className="placeholder" data-testid="connections-empty">
          <Users className="placeholder-icon" />
          <h2 className="placeholder-title">No connections yet</h2>
          <p className="placeholder-text">Connect with people whose recommendations you trust.</p>
        </div>
      ) : (
        <div className="connection-list" data-testid="connections-list">
          {connections.map((c) => (
            <div className="connection-row" key={c.id} data-testid={`connection-${c.id}`}>
              <Avatar
                userId={c.id}
                name={c.name}
                className="connection-initials"
                testId={`connection-avatar-${c.id}`}
              />
              <span className="connection-name" data-testid={`connection-name-${c.id}`}>{c.name}</span>
              <div className="owner-menu-wrap">
                <button
                  className="owner-menu-btn"
                  onClick={() => setOpenMenuId(openMenuId === c.id ? null : c.id)}
                  aria-label={`Options for ${c.name}`}
                  data-testid={`connection-menu-${c.id}`}
                >
                  <MoreVertical />
                </button>
                {openMenuId === c.id && (
                  <>
                    <div className="owner-menu-backdrop" onClick={() => setOpenMenuId(null)} />
                    <div className="owner-menu" data-testid={`connection-menu-open-${c.id}`}>
                      <button
                        className="owner-menu-item owner-menu-item-danger"
                        onClick={() => {
                          setOpenMenuId(null);
                          setConfirmTarget(c);
                        }}
                        data-testid={`remove-connection-${c.id}`}
                      >
                        Remove connection
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <section className="connect-cta" data-testid="connect-cta">
        <h2 className="connect-cta-title">Connect with someone</h2>
        <p className="connect-cta-copy">
          Send your link to someone you know. It works whether they&apos;re already on
          irecommend or they&apos;re joining for the first time.
        </p>
        <button
          className="connect-cta-btn"
          onClick={() => navigate('/invite-people')}
          data-testid="get-connection-link"
        >
          <LinkIcon className="connect-cta-icon" />
          Get connection link
        </button>
      </section>

      {confirmTarget && (
        <div
          className="limit-modal-overlay"
          onClick={() => !removing && setConfirmTarget(null)}
          data-testid="remove-connection-confirm"
        >
          <div className="limit-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="limit-modal-title">Remove {firstName}?</h3>
            <p className="limit-modal-text">
              You&apos;ll no longer see {firstName}&apos;s recommendations, including ones you&apos;ve
              saved. {FirstName} will also no longer see yours.
            </p>
            <div className="limit-modal-actions">
              <button
                className="limit-modal-btn secondary"
                onClick={() => setConfirmTarget(null)}
                disabled={removing}
                data-testid="remove-connection-cancel"
              >
                Cancel
              </button>
              <button
                className="limit-modal-btn primary"
                onClick={handleRemove}
                disabled={removing}
                data-testid="remove-connection-confirm-btn"
              >
                {removing ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
